import { podFulFillmentUtils, getChristmasDeliveryMessage } from '@thd-olt-component-react/fulfillment';
import * as constants from './constants';

const {
  CONFIGURABLE_BLINDS, HOME_DEPOT_DESIGN_CENTER, SCHEDULE_A_MEASURE_URL,
  PRODUCT_POD_V7_CLICK, CUSTOM_COUNTERTOPS
} = constants;

const {
  isLiveGoods,
  getSellableQuantityYourStore,
  isItemGCC
} = podFulFillmentUtils;

export const isBuildAndBuyProduct = (product) => {
  const { identifiers } = product || {};

  const customExperience = product?.info?.globalCustomConfigurator?.customExperience;
  const isCountertopCustomExperience = customExperience === CUSTOM_COUNTERTOPS;

  return identifiers?.productType === CONFIGURABLE_BLINDS && !isCountertopCustomExperience;
};

export const isDesignAndPurchaseProduct = (product) => {
  const { identifiers, info } = product || {};

  const customExperience = info?.globalCustomConfigurator?.customExperience === CUSTOM_COUNTERTOPS;
  const isCustomProduct = identifiers?.productType === CONFIGURABLE_BLINDS;

  return customExperience && isCustomProduct;
};

const isCustomCabinet = (productSubType) => {
  const { link = '' } = productSubType || {};
  const checkCustomCabinet = /BRIO_INAISLE_SFI_CABINETS/.test(link);
  return checkCustomCabinet;
};

export const checkScheduleAMeasureEligibility = (product) => {
  return !!(product?.installServices?.scheduleAMeasure);
};

export const isBrioProduct = (product) => {
  const { info } = product || {};
  const { isBrioSku, isCustomKitchenCabinet, productSubType } = info || {};
  return (isBrioSku && !(isCustomCabinet(productSubType) || isCustomKitchenCabinet));
};

export const isCustomKitchenCabinetProduct = (product) => {
  const { info } = product || {};
  const { isBrioSku, isCustomKitchenCabinet, productSubType } = info || {};
  return (isBrioSku && (isCustomCabinet(productSubType) || isCustomKitchenCabinet));
};

export const isLiveGoodsOOSProduct = (product) => {
  return (isLiveGoods(product) && !getSellableQuantityYourStore(product));
};

export const getCustomBlindsUrl = ({
  productType,
  blindsHost,
  canonicalUrl,
  merchQueryParam
}) => {
  const configureProduct = productType === CONFIGURABLE_BLINDS;
  const regex = /\/p\//gi;
  const customBlindsPath = '/p/custom-blinds/';
  const customProductUrl = canonicalUrl && canonicalUrl.replace(regex, customBlindsPath);
  const getCanonicalUrl = (merchQueryParam !== undefined && merchQueryParam !== null)
    ? `${canonicalUrl}?${merchQueryParam}` : `${canonicalUrl}`;

  return (configureProduct) ? `${blindsHost}${customProductUrl}` : getCanonicalUrl;
};

export const getCustomUrlWithAnalytics = ({
  productType,
  blindsHost,
  canonicalUrl,
  merchQueryParam
}) => {
  let productUrl = getCustomBlindsUrl({
    productType, blindsHost, canonicalUrl, merchQueryParam
  });

  return productUrl;
};

export const isHDDCSku = (product) => {
  return product?.details?.installation?.serviceType === HOME_DEPOT_DESIGN_CENTER
    || product?.info?.productSubType?.name === HOME_DEPOT_DESIGN_CENTER;
};

export const sendPodTapperAnalytics = (event, itemId, url, keyLabel = '') => {
  if (!window) return;

  const clickedElement = event?.target;

  // Find the PodTapper container to limit DOM traversal scope
  const podTapperContainer = clickedElement?.closest('[data-testid="pod-tapper"]');

  // Comprehensive detection: Check clicked element and ancestors for ANY interactive behavior
  const hasInteractiveElement = (element) => {
    if (!element) return false;

    // Traverse element and ancestors within PodTapper scope (max 5 levels)
    let current = element;
    let depth = 0;
    const maxDepth = 5;
    const cssSelectors = [
      '[href]',
      '[onclick]'
    ];

    // Possible key names of the react Fiber object
    const reactFiber = ['__reactFiber', '__reactInternalInstance'];
    const reactPropsOrEventHandlers = ['__reactProps', '__reactEventHandlers'];

    // will validate 6 total elements: clicked element + 5 ancestors
    while (current && depth <= maxDepth) {
      depth += 1;

      // If we reach the PodTapper container, stop here
      if (podTapperContainer && current === podTapperContainer) break;

      // Quick check: CSS selector matching for common interactive elements (fastest check first)
      for (let i = 0; i < cssSelectors.length; i += 1) {
        const selector = cssSelectors[i];
        if (current.matches && current.matches(selector)) return true;
      }

      // Check for React handlers on current element
      const reactInstance = Object.keys(current)
        .find((key) => reactFiber
          .some((fiberName) => key
            .startsWith(fiberName))
        );
      const reactProps = Object.keys(current)
        .find((key) => reactPropsOrEventHandlers
          .some((propOrEventName) => key
            .startsWith(propOrEventName))
        );

      // Access the react Fiber object
      const fiberFromInstance = reactInstance ? current[reactInstance] : null;
      const propsFromReactProps = reactProps ? current[reactProps] : null;

      if (fiberFromInstance?.memoizedProps?.onClick || propsFromReactProps?.onClick) return true;

      // Enhanced check for JavaScript-attached event listeners
      // Check for DOM onclick or library-attached events (e.g., jQuery)
      const hasClickListener = current.onclick || current._events?.click;

      if (hasClickListener) return true;
      current = current.parentElement;
    }
    return false;
  };

  // Check for any interactive behavior (comprehensive single check)
  const hasInteractiveElementFound = hasInteractiveElement(clickedElement);

  if (hasInteractiveElementFound) {
    // Interactive element found: Let the existing click handler manage navigation and analytics
    // This prevents interference with buttons, links, and other interactive components
    return; // Exit early, don't preventDefault or trigger pod tapper analytics
  }

  // No interactive elements found within the pod tapper scope (child elements)
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const podTapperEventData = {
    podAction: 'pod tapper',
    podAnchorSku: itemId,
    target: '_self',
    parent: 'plp',
    podKey: keyLabel.toLowerCase(),
  };
  window.LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, podTapperEventData);
  setTimeout(() => {
    window.location.href = url;
  }, 100);
};

export const isChristmasMessagingEligible = (product, christmasBuffer) => {
  return !isItemGCC(product) && getChristmasDeliveryMessage(
    { fulfillment: product?.fulfillment, bufferDays: christmasBuffer, checkArrivesAfter: false }
  ) === 'Delivery by Christmas';
};
